import type { MouseEvent, PropsWithChildren } from "react";

import { ArrowDown, ClearCircle } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import type { FlexProps } from "@similarweb/ui-components/foundations";
import { Flex, Text } from "@similarweb/ui-components/foundations";

const { carbon } = colorsPalettes;

export const ChipContent = ({
    isSelected,
    children,
    onReset,
}: PropsWithChildren<{
    isSelected?: boolean;
    onReset?: () => void;
}>) => (
    <Text
        fontVariant="chip1"
        color={!isSelected ? carbon[500] : carbon[0]}
        display="inline-flex"
        alignItems="center"
        gap="4px"
    >
        {children}
        {!isSelected ? (
            <ArrowDown
                color={carbon[300]}
                size="sm"
            />
        ) : (
            <ClearCircle
                color={carbon[0]}
                size="sm"
                onClick={(event: MouseEvent<SVGSVGElement>) => {
                    event.stopPropagation();
                    onReset?.();
                }}
                data-automation="chip-reset-button"
            />
        )}
    </Text>
);

export const Chip = ({
    isSelected,
    onReset,
    children,
    ...props
}: PropsWithChildren<{
    isSelected?: boolean;
    onReset?: () => void;
}> &
    FlexProps) => (
    <Flex
        as="button"
        type="button"
        border="none"
        borderRadius="40px"
        background={isSelected ? rgba(carbon[500], 0.8) : rgba(carbon[200], 0.2)}
        alignItems="center"
        gap="4px"
        padding="4px 4px 4px 12px"
        whenHover={{
            background: isSelected ? carbon[500] : rgba(carbon[500], 0.1),
        }}
        {...props}
    >
        <ChipContent
            isSelected={isSelected}
            onReset={onReset}
        >
            {children}
        </ChipContent>
    </Flex>
);
